import { useState, useRef, useCallback, useMemo, useLayoutEffect, useEffect } from 'react'
import useGraphCandleWindow from './useGraphCandleWindow'
import useBacktestProgress from './useBacktestProgress'
import useEquityCurve from './useEquityCurve'
import usePlayback, { binarySearchFirstAfter } from './usePlayback'
import useFormingBar from './useFormingBar'
import { nextFollow } from './replayFollow'
import { getCandleCountForTimeframe, formatDateLabel } from '../../utils/chartUtils'
import { parseProgressTimestamp } from '../utils/timeParsing'

const USER_CHART_NAVIGATION_WINDOW_MS = 500

export const AVAILABLE_SPEEDS: readonly number[] = [0.5, 1, 2, 5, 10, 25, 50]

/**
 * Speeds at which replay animates the forming candle from sub-bar data instead of
 * revealing whole bars. Speed is the ONLY control for this — there is no separate
 * toggle — so this set is the entire policy.
 *
 * Edit this one line to change it: drop 2 to make it stricter, or add 3/4 to go
 * further (a brand-new speed also has to be added to AVAILABLE_SPEEDS above).
 * Above these speeds a bar dwells too briefly for a build-up to read, and the
 * whole-bar reveal is both cheaper and clearer.
 */
export const TICK_REPLAY_SPEEDS: ReadonlySet<number> = new Set([0.5, 1, 2])

interface UseBacktestChartStateParams {
  btid: string
  symbol: string
  initialTimeframe: string
  initialStatus: number
  initialProgressBacktest: string | null
  initialProgressView: string | null
  rangeStartTs: number
  rangeEndTs: number
  /**
   * Starting playback speed. Defaults to 10 — fast enough to scan a year of backtest.
   *
   * The walkthrough passes a speed inside TICK_REPLAY_SPEEDS, because it is watched bar by
   * bar to check the code fires where the user drew; at 10x the sub-bar animation never
   * engages and a bar dwells too briefly to read.
   */
  initialSpeed?: number
  /**
   * Speed owned by the CALLER, overriding the hook's own state.
   *
   * The walkthrough derives it from its detail-plus-pace controls, so the value has to be
   * able to change after mount — which `initialSpeed` cannot express. Omit it and the hook
   * keeps its own state, seeded from `initialSpeed` and moved by `handleSpeedChange`, which
   * is what the Backtest tab's speed picker drives.
   */
  speed?: number
  /**
   * Where "Restart" lands, and the fallback cursor when nothing was saved. Defaults to
   * rangeStartTs. See usePlayback's replayStartTs for why the walkthrough overrides it.
   */
  replayStartTs?: number
  /**
   * Where 0% sits on the transport. Defaults to rangeStartTs.
   *
   * These are the same for a backtest and DIFFERENT for a walkthrough, which is the whole
   * reason this exists: the server pads a walkthrough's range with ~200 bars of indicator
   * warm-up before the drawn span, so measuring progress from rangeStartTs opened a fresh
   * run at roughly (200 - lead) / (200 + drawn + 20) — near or past 50%, on a run that had
   * not started. The user watches from replayStartTs, so that is what 0% has to mean.
   */
  progressWindowStartTs?: number
  /**
   * Whether the bar being revealed animates as it forms. Defaults to on.
   *
   * The walkthrough drives this from its Detail control instead of from the speed, because
   * once speed means "how fast" and detail means "how finely", a speed-membership test
   * cannot answer it — candle detail's slowest pace and tick detail's slowest pace want the
   * same dwell time and opposite answers.
   */
  formingBarEnabled?: boolean
  /**
   * Which speeds animate the forming bar, when `formingBarEnabled`. Defaults to
   * TICK_REPLAY_SPEEDS — the Backtest tab's policy, where speed is the only control.
   */
  tickReplaySpeeds?: ReadonlySet<number>
  /** Wall-clock ms per bar, for the walkthrough's Tick detail. See usePlayback. */
  barIntervalMsOverride?: number | null
  /**
   * Start playing on its own, as soon as the candle window is loaded. Defaults to off.
   *
   * Read ONCE, at mount — it seeds the same `autoplayPending` request that Restart raises, so it
   * inherits that path's waiting: play() only fires when the candles for the cursor are in and
   * the button would actually do something, and a user who pans or presses anything first
   * cancels it. A later change to this flag does nothing, which is what "on ready" means; a
   * caller that wants to start a second time has the transport for that.
   */
  autoPlayOnReady?: boolean
  /**
   * Whether panning, zooming or touching the chart pauses the replay. Defaults to ON.
   *
   * The Backtest tab has always worked this way: that chart is something you SCAN, so touching
   * it means "stop, I want to look". The walkthrough is the opposite — it exists to be watched
   * RUNNING, and a user who scrolls back to check a level expects the run to carry on.
   *
   * Turning it off changes TWO things and has to change both. Chart gestures stop reaching the
   * transport, AND the play loop stops right-aligning the viewport unconditionally — because
   * without the second half the first is worthless: the loop re-pins the view every 100-250ms,
   * so a pan would be erased faster than the user could see it and their zoom would be replaced
   * by the timeframe's default. See `followVisibleRange`.
   */
  pauseOnChartNavigation?: boolean
}

export default function useBacktestChartState({
  btid,
  symbol,
  initialTimeframe,
  initialStatus,
  initialProgressBacktest,
  initialProgressView,
  rangeStartTs,
  rangeEndTs,
  initialSpeed = 10,
  speed: controlledSpeed,
  replayStartTs,
  progressWindowStartTs,
  formingBarEnabled = true,
  tickReplaySpeeds = TICK_REPLAY_SPEEDS,
  barIntervalMsOverride = null,
  autoPlayOnReady = false,
  pauseOnChartNavigation = true,
}: UseBacktestChartStateParams) {
  const rangeTotal = rangeEndTs - rangeStartTs
  const progressWindowStart = progressWindowStartTs ?? rangeStartTs
  const progressWindowTotal = Math.max(1, rangeEndTs - progressWindowStart)
  const [backtestStatus, setBacktestStatus] = useState(initialStatus)
  const [timeframe, setTimeframe] = useState<string>(initialTimeframe)
  const [internalSpeed, setSpeed] = useState<number>(initialSpeed)
  const speed = controlledSpeed ?? internalSpeed
  // Queued auto-play request. Set when the user presses Reset ("Restart
  // replay"), or at mount by autoPlayOnReady; consumed by the autoplay-fire
  // effect below once the candle window is loaded (isLoading=false) and the
  // play button would actually do something (not at the buffered boundary).
  const [autoplayPending, setAutoplayPending] = useState(autoPlayOnReady)

  const initialProgressViewTs = useMemo(() => {
    const saved = parseProgressTimestamp(initialProgressView)
    if (saved !== null) return saved
    // A caller that named its own replay start means it: open there rather than on the
    // fully-drawn end state. The walkthrough relies on this — it exists to be WATCHED, so
    // landing on the finished chart would skip the whole point.
    if (replayStartTs != null) return replayStartTs
    // Completed backtest that was never replayed: land on the fully-drawn
    // end state instead of an empty chart at the range start.
    const isInitiallyCompleted = initialStatus !== 0 && initialStatus !== 1
    return isInitiallyCompleted ? rangeEndTs : rangeStartTs
  }, [initialProgressView, initialStatus, rangeStartTs, rangeEndTs, replayStartTs])
  const initialProgressBacktestTs = useMemo(
    () => parseProgressTimestamp(initialProgressBacktest),
    [initialProgressBacktest],
  )

  const [progressViewTs, setProgressViewTs] = useState(initialProgressViewTs)
  const progressViewTsRef = useRef(progressViewTs)
  useLayoutEffect(() => { progressViewTsRef.current = progressViewTs }, [progressViewTs])
  const programmaticRangeRef = useRef<{ from: number; to: number } | null>(null)
  const rangeChangeSuppressedRef = useRef(false)
  const lastUserChartNavigationAtRef = useRef(0)

  const handleSliderJump = useCallback((targetTs: number) => {
    const currentProgress = progressViewTsRef.current
    if (targetTs > currentProgress) {
      setProgressViewTs(targetTs)
    }
  }, [])

  const {
    candles,
    isLoading,
    loadError,
    orders,
    effectiveCandleCount,
    chartRef,
    handleVisibleRangeChange: gwHandleVisibleRangeChange,
    maxAvailableTs,
    isInProgress,
    progressPercent,
    maxSliderFraction,
    updateBacktestProgress,
    sliderPosition,
    sliderBarRef,
    handleSliderMouseDown: gwHandleSliderMouseDown,
    windowIndicator,
    sliderDateLabel,
    dateRangeLabel,
    loadWindow,
    extendWindow,
    applyVisibleRange,
    buildVisibleRange,
    maxAvailableTsRef,
  } = useGraphCandleWindow({
    symbol,
    timeframe,
    rangeStartTs,
    rangeEndTs,
    btid,
    programmaticRangeRef,
    rangeChangeSuppressedRef,
    initialMaxAvailableTs: initialProgressBacktestTs,
    displayBoundaryTs: progressViewTs,
    initialCenterTs: initialProgressViewTs > rangeStartTs ? initialProgressViewTs : null,
    onSliderJump: handleSliderJump,
  })

  /** Whether the replay still owns the viewport. Only consulted when gestures do not pause. */
  const followViewportRef = useRef(true)
  /** Bars on screen at the last tick, so a zoom can be told from a pan. */
  const lastVisibleBarCountRef = useRef<number | null>(null)

  /**
   * The play loop's viewport write, filtered through "is the user still watching the playhead?".
   *
   * A pass-through when gestures pause — the Backtest tab's loop keeps writing exactly the range
   * it always wrote. When they do NOT pause it is the other half of that policy: it holds the
   * user's own zoom, and it declines to write at all once they have scrolled the newest bar out
   * of view. Nothing else is needed to keep a scrolled-back view still: the engine holds it on
   * its own, because appending a bar while the right-side offset is negative decrements it
   * (klinecharts ChartStore.addData).
   *
   * The three reads happen in one instant on purpose. A head-prune or a history page re-indexes
   * the candles, and reading the playhead and the viewport at the same moment is what keeps the
   * distance between them — the only thing nextFollow judges — immune to that shift.
   */
  const followVisibleRange = useCallback((range: { from: number; to: number }) => {
    if (pauseOnChartNavigation) {
      applyVisibleRange(range)
      return
    }
    const chart = chartRef.current
    const barCount = chart?.getVisibleBarCount() ?? null
    const decision = nextFollow({
      requested: range,
      current: chart?.getVisibleLogicalRange() ?? null,
      newestIdx: (chart?.getCandleCount() ?? 0) - 1,
      barCount,
      prevBarCount: lastVisibleBarCountRef.current,
      following: followViewportRef.current,
    })
    lastVisibleBarCountRef.current = barCount
    followViewportRef.current = decision.following
    if (decision.range) applyVisibleRange(decision.range)
  }, [pauseOnChartNavigation, applyVisibleRange, chartRef])

  useBacktestProgress({
    btid,
    backtestStatus,
    initialProgressBacktest,
    rangeStartTs,
    rangeEndTs,
    updateBacktestProgress,
    onStatusChange: setBacktestStatus,
  })

  const isBacktestCompleted = backtestStatus !== 0 && backtestStatus !== 1

  const { points: equityPoints, runningTotals: equityRunningTotals } = useEquityCurve({
    btid,
    maxAvailableTs: maxAvailableTs ?? null,
  })

  const candlesRef = useRef(candles)
  useLayoutEffect(() => { candlesRef.current = candles }, [candles])

  const playback = usePlayback({
    btid,
    rangeStartTs,
    rangeEndTs,
    progressViewTs,
    onProgressViewChange: setProgressViewTs,
    timeframe,
    speed,
    candlesRef,
    maxAvailableTsRef,
    extendWindow,
    loadWindow,
    applyVisibleRange: followVisibleRange,
    replayStartTs,
    barIntervalMsOverride,
  })

  const { isPlaying, playStartedAtMs, playElapsedSec } = playback

  // Sub-bar animation of the bar replay is about to reveal. Purely additive: it
  // paints through the chart handle and never touches replay state, so nothing
  // above or below it changes behaviour when it is inactive.
  const formingBar = useFormingBar({
    enabled: formingBarEnabled,
    symbol,
    timeframe,
    speed,
    isPlaying,
    progressViewTs,
    candlesRef,
    maxAvailableTsRef,
    chartRef,
    tickReplaySpeeds,
    barIntervalMsOverride,
  })

  const displayedCandles = useMemo(() => {
    if (candles.length === 0 || effectiveCandleCount === candles.length) return candles
    return candles.slice(0, effectiveCandleCount)
  }, [candles, effectiveCandleCount])

  const displayedOrders = useMemo(() => {
    const boundary = maxAvailableTs !== null
      ? Math.min(progressViewTs, maxAvailableTs)
      : progressViewTs
    if (orders.length === 0) return orders
    return orders.filter(o => o.time <= boundary)
  }, [orders, progressViewTs, maxAvailableTs])

  const equityDisplayBoundaryTs = useMemo(
    () => maxAvailableTs !== null ? Math.min(progressViewTs, maxAvailableTs) : progressViewTs,
    [progressViewTs, maxAvailableTs],
  )

  const currentTimestamp = useMemo(
    () => Math.floor(rangeStartTs + sliderPosition * rangeTotal),
    [rangeStartTs, sliderPosition, rangeTotal],
  )

  /**
   * `maxSliderFraction` in the PROGRESS window's space.
   *
   * The cap is produced against the full range, so it cannot be compared against a fraction
   * measured from a later start — a walkthrough legitimately 80% through its own window
   * would be pinned to the ~50% the padded range says the engine has reached. Round-trip it
   * through a timestamp instead. Identity when the two starts coincide, which is every
   * caller but the walkthrough.
   */
  const maxProgressFraction = useMemo(() => {
    if (maxSliderFraction >= 1) return 1
    const cappedTs = rangeStartTs + maxSliderFraction * rangeTotal
    return Math.max(0, Math.min(1, (cappedTs - progressWindowStart) / progressWindowTotal))
  }, [maxSliderFraction, rangeStartTs, rangeTotal, progressWindowStart, progressWindowTotal])

  const progressViewPosition = useMemo(
    () => Math.max(
      0,
      Math.min(maxProgressFraction, (progressViewTs - progressWindowStart) / progressWindowTotal),
    ),
    [maxProgressFraction, progressViewTs, progressWindowStart, progressWindowTotal],
  )

  const progressViewDateLabel = useMemo(
    () => formatDateLabel(progressViewTs),
    [progressViewTs],
  )

  const visibleCount = useMemo(
    () => getCandleCountForTimeframe(timeframe, displayedCandles.length),
    [timeframe, displayedCandles.length],
  )

  const isPlayingRef = useRef(false)
  useLayoutEffect(() => { isPlayingRef.current = isPlaying }, [isPlaying])

  // PLAY MEANS FOLLOW AGAIN. Pressing play after scrolling back to a trade — or after the trades
  // pane scrolled there for you — is a request to watch the run, and watching it means seeing the
  // bar it is on. The first tick then snaps to the playhead at whatever zoom the user left,
  // because followVisibleRange takes the span from the engine rather than from the timeframe.
  useLayoutEffect(() => {
    if (isPlaying) followViewportRef.current = true
  }, [isPlaying])

  const handleChartUserNavigate = useCallback(() => {
    // Chart gestures are not transport commands here — see pauseOnChartNavigation. Not even the
    // autoplay cancel: a run that was about to start itself should still start, and the viewport
    // follow (followVisibleRange) is what keeps that from stomping the gesture.
    if (!pauseOnChartNavigation) return
    // User panned/dragged the chart — cancel any queued auto-play so we don't
    // surprise them by starting playback after they actively navigated away.
    setAutoplayPending(false)
    lastUserChartNavigationAtRef.current = Date.now()
    if (isPlayingRef.current) {
      playback.pause()
    }
  }, [playback, pauseOnChartNavigation])

  const handleVisibleRangeChange = useCallback((range: { from: number; to: number }) => {
    const now = Date.now()
    const isSuppressed = rangeChangeSuppressedRef.current
    const expectedRange = programmaticRangeRef.current
    const isRecentUserNavigation =
      now - lastUserChartNavigationAtRef.current < USER_CHART_NAVIGATION_WINDOW_MS
    if (isSuppressed) return

    if (expectedRange) {
      const matchesProgrammaticRange =
        Math.abs(range.from - expectedRange.from) < 1 &&
        Math.abs(range.to - expectedRange.to) < 1
      if (matchesProgrammaticRange) {
        programmaticRangeRef.current = null
        return
      }
    }

    // Naturally dead when pauseOnChartNavigation is off: the stamp this reads is only ever
    // written by handleChartUserNavigate, which returns before setting it.
    if (isPlayingRef.current && isRecentUserNavigation) {
      lastUserChartNavigationAtRef.current = 0
      playback.pause()
    }
    gwHandleVisibleRangeChange(range)
  }, [gwHandleVisibleRangeChange, playback])

  const handleSliderMouseDown = useCallback((e: React.MouseEvent) => {
    if (isPlayingRef.current) {
      playback.pause()
    }
    gwHandleSliderMouseDown(e)
  }, [gwHandleSliderMouseDown, playback])

  const handlePlay = useCallback(() => {
    // lastUserChartNavigationAtRef.current = 0
    playback.play()
  }, [playback])

  const handlePause = useCallback(() => {
    // Explicit user pause — drop any queued auto-play.
    setAutoplayPending(false)
    playback.pause()
  }, [playback])

  // Disabled when the replay is at the buffered boundary (either the end of
  // the configured range or the latest candle the backtest has produced).
  // Without the maxAvailableTs cap, pressing play while the backtest is still
  // catching up would just spin in the play-loop's wait-for-data branch.
  const playBoundaryTs = maxAvailableTs ?? rangeEndTs
  const isPlayDisabled = progressViewTs >= playBoundaryTs

  // ---- Auto-play orchestration ----
  // Hold play()/jumpTo() in refs so the effects below don't need playback in
  // their deps. Both are technically stable via useCallback, but routing
  // through refs insulates this from future signature changes.
  const playRef = useRef(playback.play)
  useLayoutEffect(() => { playRef.current = playback.play }, [playback.play])
  const jumpToRef = useRef(playback.jumpTo)
  useLayoutEffect(() => { jumpToRef.current = playback.jumpTo }, [playback.jumpTo])

  // Detect the "backtest just became ready" transition (PENDING/RUNNING -> any
  // terminal status). Failed/cancelled statuses (200/201) never reach this
  // hook — BotBacktest.tsx renders a different UI for them — so any
  // active->terminal transition here means a successful completion.
  const prevStatusRef = useRef(initialStatus)
  useEffect(() => {
    const prev = prevStatusRef.current
    prevStatusRef.current = backtestStatus
    const wasActive = prev === 0 || prev === 1
    const isActiveNow = backtestStatus === 0 || backtestStatus === 1
    if (wasActive && !isActiveNow) {
      // Backtest just completed: land on the fully-drawn chart at the end.
      // Playing is a deliberate act from here (the "Restart replay" button).
      // If the user is actively replaying mid-run, don't yank them — the play
      // loop naturally runs to the end and stops there anyway.
      if (isPlayingRef.current) return
      jumpToRef.current(rangeEndTs)
    }
  }, [backtestStatus, rangeEndTs])

  // Fire the queued auto-play once it's safe to do so. The effect runs after
  // React commits, so isLoading=false guarantees the candle window for
  // progressViewTs is already in candlesRef — play() will skip its
  // isCurrentWindowLoaded fallback and NOT trigger a duplicate loadWindow.
  useEffect(() => {
    if (!autoplayPending) return
    if (isLoading) return
    if (isPlayDisabled) {
      // Nothing to play (already at the buffered boundary). Drop the request
      // so we don't surprise the user later when isLoading flips again.
      // eslint-disable-next-line react-hooks/set-state-in-effect -- one-shot signal: clearing the flag IS the effect's purpose
      setAutoplayPending(false)
      return
    }
    // Consume the queued auto-play. The follow-up effect run early-returns at
    // `if (!autoplayPending) return`, so we don't get a feedback loop.
    setAutoplayPending(false)
    playRef.current()
  }, [autoplayPending, isLoading, isPlayDisabled])

  const handleSpeedChange = useCallback((nextSpeed: number) => { setSpeed(nextSpeed) }, [])

  const handleCycleSpeed = useCallback(() => {
    setSpeed(prev => {
      const list = AVAILABLE_SPEEDS
      const idx = list.indexOf(prev)
      const nextIdx = idx === -1 ? 0 : (idx + 1) % list.length
      return list[nextIdx]
    })
  }, [])

  const handleReset = useCallback(() => {
    // Reset to rangeStart and queue an auto-play. The fire effect waits for
    // reset's own loadWindow to finish (isLoading flips back to false) before
    // calling play(), which avoids the duplicate-loadWindow race the original
    // implementation had.
    playback.reset()
    setAutoplayPending(true)
  }, [playback])

  const handleTimeframeChange = useCallback((tf: string) => {
    playback.pause()
    setTimeframe(tf)
  }, [playback])

  /**
   * Move the replay exactly one bar. Used by the walkthrough transport's step buttons, where
   * the user wants to inspect the bar an entry fired on.
   *
   * Reuses usePlayback's own binarySearchFirstAfter so the notion of "the next bar" cannot
   * drift from the one the play loop and the forming-bar animation use. Forward steps stop at
   * the buffered boundary for the same reason play() does.
   */
  const handleStep = useCallback((delta: 1 | -1) => {
    const candles = candlesRef.current ?? []
    if (candles.length === 0) return

    // The cursor sits on bar k, so binarySearchFirstAfter gives k+1: forward is that bar,
    // backward is k-1.
    const next = binarySearchFirstAfter(candles, progressViewTsRef.current)
    const idx = delta > 0 ? next : next - 2
    const clamped = Math.max(0, Math.min(candles.length - 1, idx))
    const targetTs = candles[clamped].time

    if (delta > 0 && targetTs > (maxAvailableTsRef.current ?? rangeEndTs)) return

    setAutoplayPending(false)
    playback.jumpTo(targetTs, { keepWindow: true })

    // Scroll one bar, the same way the play loop keeps the newest revealed bar in view.
    const visible = getCandleCountForTimeframe(timeframe, clamped + 1)
    applyVisibleRange({ from: Math.max(0, clamped - visible + 1), to: clamped })
  }, [playback, maxAvailableTsRef, rangeEndTs, timeframe, applyVisibleRange, candlesRef])

  /**
   * Scrub to an absolute time: reveal to there, and TAKE THE VIEWPORT WITH YOU.
   *
   * The second half is the part that was missing. `jumpTo` moves the reveal boundary and
   * nothing else, so a forward scrub grew the revealed set while leaving the visible range
   * exactly where it was — the new bars landed off-screen to the right and the chart looked
   * frozen under a moving thumb. Following the playhead here is the same thing the play loop
   * (usePlayback) and the step buttons (handleStep) already do, computed the same way, so all
   * three ways of moving the cursor scroll identically.
   *
   * `keepWindow` for the same reason handleStep uses it, only more so: a native range input
   * emits dozens of change events per drag, and without it each one fires a full window
   * refetch whose result is then discarded by the next — so the viewport could not move until
   * the last request landed, seconds behind the thumb. A target genuinely outside the loaded
   * candles still reloads, which is the option's own contract.
   */
  const handleEquityTimeClick = useCallback((timestamp: number) => {
    // Scrubbing is an explicit "I want to look here, paused" intent — drop
    // any queued auto-play so a recently-queued one doesn't fire mid-scrub.
    setAutoplayPending(false)
    playback.jumpTo(timestamp, { keepWindow: true })

    const candles = candlesRef.current ?? []
    if (candles.length === 0) return
    // Re-apply jumpTo's own floor, so the range we scroll to is the bar it actually landed on.
    const ts = Math.max(replayStartTs ?? rangeStartTs, timestamp)
    // The newest revealed bar: the one before the first bar strictly after the cursor.
    const idx = Math.max(0, Math.min(candles.length - 1, binarySearchFirstAfter(candles, ts) - 1))
    const visible = getCandleCountForTimeframe(timeframe, idx + 1)
    applyVisibleRange({ from: Math.max(0, idx - visible + 1), to: idx })
  }, [playback, candlesRef, replayStartTs, rangeStartTs, timeframe, applyVisibleRange])

  /**
   * Bring a past moment into view WITHOUT moving the playhead.
   *
   * Sibling of handleEquityTimeClick and deliberately not the same thing. That one belongs to
   * the scrubber and moves the reveal cursor; this one only scrolls — which is what "show me
   * that trade" means. The replay keeps its place: looking at a trade that has already happened
   * is not the same act as winding the run back to it, and one cursor moved by two different
   * intents is how a user loses the position they had played to.
   *
   * Centred rather than right-aligned, via useGraphCandleWindow's own buildVisibleRange: the
   * target is behind the playhead, so there is revealed chart on both sides of it to show — and
   * that function's clamp keeps the range inside what is revealed.
   */
  const handleShowTime = useCallback((timestamp: number) => {
    // A running loop re-applies its own right-aligned range on the next tick and would undo
    // this scroll a fraction of a second after it lands, so the click takes the transport with
    // it. handlePause also drops any queued autoplay, which would do the same thing later.
    if (isPlayingRef.current) handlePause()
    const range = buildVisibleRange(timestamp)
    if (range) applyVisibleRange(range)
  }, [handlePause, buildVisibleRange, applyVisibleRange])

  return {
    timeframe,
    isPlaying,
    speed,
    backtestStatus,
    isBacktestCompleted,
    candles: displayedCandles,
    orders: displayedOrders,
    isLoading,
    loadError,
    chartRef,
    maxAvailableTs,
    isInProgress,
    progressPercent,
    maxSliderFraction,
    progressViewPosition,
    progressViewDateLabel,
    equityDisplayBoundaryTs,
    sliderPosition,
    sliderBarRef,
    windowIndicator,
    sliderDateLabel,
    dateRangeLabel,
    visibleCount,
    currentTimestamp,
    equityPoints,
    equityRunningTotals,
    isPlayDisabled,
    handleVisibleRangeChange,
    handleChartUserNavigate,
    handleSliderMouseDown,
    handlePlay,
    handlePause,
    handleReset,
    handleSpeedChange,
    handleCycleSpeed,
    handleTimeframeChange,
    handleEquityTimeClick,
    handleShowTime,
    handleStep,
    playStartedAtMs,
    playElapsedSec,
    progressViewTs,
    tickReplay: {
      // The caller's set, not the module default: a caller that overrode it and then read
      // back the constant would mark the wrong speeds as animating in its own speed menu.
      speeds: tickReplaySpeeds,
      active: formingBar.active,
      available: formingBar.available,
    },
  }
}
